// POCreate.jsx
// Create new purchase order form

import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import VendorAPI from '../../../services/vendorService';
import PurchasingAPI from '../../../services/purchasingService';
import { isMultiLocation, getLocationName } from '../../../util/locationUtils';

const DESKTOP_BREAKPOINT = 1024;

class POCreate extends Component {
    state = {
        vendors: [],
        form: {
            vendor_id: '',
            location_id: this.props.selectedLocationId || '',
            expected_date: '',
            notes: ''
        },
        saving: false,
        error: null,
        loading: true,
        isDesktop: window.innerWidth >= DESKTOP_BREAKPOINT
    };

    async componentDidMount() {
        window.addEventListener('resize', this.handleResize);
        try {
            const vendors = await VendorAPI.getVendors(this.props.typeId);
            // Filter to active vendors
            const activeVendors = (Array.isArray(vendors) ? vendors : [])
                .map(v => ({ ...v, vendor_id: v.vendor_id || v.id }))
                .filter(v => v.is_active !== false);
            this.setState({ vendors: activeVendors, loading: false });

            // Pre-fill vendor from query param if provided
            const params = new URLSearchParams(this.props.location.search);
            const vendorId = params.get('vendorId');
            if (vendorId) {
                this.handleChange('vendor_id', vendorId);
            }
        } catch (err) {
            console.error('Error loading vendors:', err);
            this.setState({ error: 'Failed to load vendors.', loading: false });
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }

    handleResize = () => {
        this.setState({ isDesktop: window.innerWidth >= DESKTOP_BREAKPOINT });
    };

    handleChange = (field, value) => {
        this.setState(prev => {
            const form = { ...prev.form, [field]: value };
            // Auto-fill expected date based on vendor lead time
            if (field === 'vendor_id' && value) {
                const vendor = prev.vendors.find(v => String(v.vendor_id) === String(value));
                if (vendor && vendor.lead_time_days) {
                    const date = new Date();
                    date.setDate(date.getDate() + vendor.lead_time_days);
                    form.expected_date = date.toISOString().split('T')[0];
                }
            }
            return { form };
        });
    };

    handleCreate = async () => {
        const { form } = this.state;
        if (!form.vendor_id) {
            this.setState({ error: 'Please select a vendor.' });
            return;
        }
        this.setState({ saving: true, error: null });
        try {
            const vendorId = parseInt(form.vendor_id, 10);
            const locationId = form.location_id ? parseInt(form.location_id, 10) : null;
            const payload = {
                installer_id: this.props.typeId,
                vendor_id: vendorId,
                location_id: !isNaN(locationId) ? locationId : null,
                expected_date: form.expected_date || null,
                notes: form.notes || null
            };
            console.log('[POCreate] Creating PO with payload:', JSON.stringify(payload, null, 2));
            const result = await PurchasingAPI.createPurchaseOrder(payload);
            const poId = result.purchase_order_id || result.id || (result.data && (result.data.purchase_order_id || result.data.id));
            this.props.history.push(`/purchase-orders/${poId}`);
        } catch (err) {
            console.error('Error creating PO:', err);
            const serverMsg = err?.response?.data?.error || err?.response?.data?.message || '';
            console.error('[POCreate] Server response:', JSON.stringify(err?.response?.data, null, 2));
            this.setState({ saving: false, error: serverMsg ? `Failed to create purchase order: ${serverMsg}` : 'Failed to create purchase order.' });
        }
    };

    render() {
        const { vendors, form, saving, error, loading, isDesktop } = this.state;
        const { locations } = this.props;
        const multiLoc = isMultiLocation(locations);

        return (
            <div className={`po-create-container ${!isDesktop ? 'mobile' : ''}`}>
                <style>{`
                    .po-create-container {
                        max-width: 800px;
                        margin: 0 auto;
                        padding: 24px;
                        background: var(--color-bg-panel);
                        min-height: 100vh;
                    }
                    .po-create-container.mobile { padding: 16px; }
                    .po-create-card {
                        background: var(--color-bg-card);
                        border: 1px solid var(--color-border);
                        border-radius: 8px;
                        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                        padding: 24px;
                    }
                    .po-create-card h4 {
                        font-size: 20px;
                        font-weight: 600;
                        color: var(--color-text-primary);
                        margin-bottom: 20px;
                    }
                    .action-btn {
                        background: var(--color-bg-card);
                        border: 1px solid var(--color-border);
                        color: var(--color-text-secondary);
                        padding: 8px 16px;
                        border-radius: 6px;
                        font-size: 13px;
                        font-weight: 500;
                        cursor: pointer;
                    }
                    .action-btn.primary-btn {
                        background: var(--color-brand-teal);
                        color: var(--color-text-inverse);
                        border-color: var(--color-brand-teal);
                    }
                    .action-btn.primary-btn:hover { background: var(--color-brand-teal-hover); }
                `}</style>

                {/* Tab Toggle */}
                <div className="text-center mb-3">
                    <div className="btn-group" role="group">
                        <Link
                            to="/purchasing"
                            className="btn btn-primary"
                            style={{ textDecoration: 'none' }}>
                            PURCHASING
                        </Link>
                        <Link
                            to="/purchasing?tab=vendors"
                            className="btn btn-outline-primary"
                            style={{ textDecoration: 'none' }}>
                            VENDORS
                        </Link>
                    </div>
                </div>

                <div style={{ marginBottom: '16px', fontSize: '14px' }}>
                    <Link to="/purchasing" style={{ color: 'var(--color-brand-teal)', textDecoration: 'none' }}>
                        <i className="fa-sharp fa-regular fa-arrow-left mr-2"></i>Return to Purchase Orders
                    </Link>
                </div>

                <div className="po-create-card">
                    <h4><i className="fa-sharp fa-regular fa-file-plus mr-2"></i>Create Purchase Order</h4>

                    {error && <div className="alert alert-danger">{error}</div>}

                    {loading ? (
                        <div className="text-center py-4">
                            <i className="fa-sharp fa-regular fa-spinner fa-spin fa-2x"></i>
                        </div>
                    ) : (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>Vendor <span className="text-danger">*</span></Form.Label>
                                <Form.Control as="select"
                                    value={form.vendor_id}
                                    onChange={(e) => this.handleChange('vendor_id', e.target.value)}>
                                    <option value="">Select a vendor...</option>
                                    {vendors.map(v => (
                                        <option key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</option>
                                    ))}
                                </Form.Control>
                            </Form.Group>

                            {multiLoc && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Location</Form.Label>
                                    <Form.Control as="select"
                                        value={form.location_id}
                                        onChange={(e) => this.handleChange('location_id', e.target.value)}>
                                        <option value="">All Locations</option>
                                        {(locations || []).map(loc => (
                                            <option key={loc.location_id} value={loc.location_id}>
                                                {getLocationName(loc)}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label>Expected Delivery Date</Form.Label>
                                <Form.Control type="date"
                                    value={form.expected_date}
                                    onChange={(e) => this.handleChange('expected_date', e.target.value)} />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Notes</Form.Label>
                                <Form.Control as="textarea" rows={3}
                                    value={form.notes}
                                    onChange={(e) => this.handleChange('notes', e.target.value)} />
                            </Form.Group>

                            <div className="d-flex justify-content-end" style={{ gap: '12px' }}>
                                <button className="action-btn" onClick={() => this.props.history.push('/purchasing')}>Cancel</button>
                                <button className="action-btn primary-btn" onClick={this.handleCreate} disabled={saving}>
                                    {saving ? (
                                        <><i className="fa-sharp fa-regular fa-spinner fa-spin mr-2"></i>Creating...</>
                                    ) : (
                                        <><i className="fa-sharp fa-regular fa-plus mr-2"></i>Create &amp; Add Lines</>
                                    )}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }
}

export default POCreate;
